import React, { useEffect, useState, Fragment } from 'react'
import queryString from 'query-string'
import DataTable from '../../../../../app/components/statelessDataTable'
import DefaultErrorMessage from '../../../../../common/components/ErrorBoundary/errorMessage'
import createColumns from '../columns/columns'
import LEGACY_PRODUCTS from '../../../../../common/enum/legacyProducts'
import useSummaries from '../../../../hooks/useSummaries'
import { usePaidLeave } from '../../../../../context/paidLeaveContext'
import { getCompanyId, getProductById } from '../../../../helper'
import { filterLocales } from '../../../../../common/helpers'
import { useAuth } from '../../../../../auth/useAuth'
import { SORT_OPTIONS } from '../../../../constants'
import { OUTPUT_TYPE } from '../../../../../common/enum/export'
import useIsMounted from '../../../../../hooks/useIsMounted'
import { CSVDownloadError } from '../../../../../common/components/CSVDownloadError/CSVDownloadError'


const DEFAULT_VALUES = Object.freeze({
  PAGE_NUMBER: 1,
  ROWS_PER_PAGE: 20,
  SEARCH_TEXT: '',
  FILTERS: { state: '' },
  SORT: {
    field: 'name',
    direction: 'asc'
  },
  FILTER_OPTIONS: {
    filter: '',
    field: '',
    paidLeaveType: 'paidSickLeave'
  }
})

const PaidLeaveSummaryTable = (props) => {
  const { current: isMounted } = useIsMounted()
  const {
    paidLeaveData,
    setSelectedProduct
  } = usePaidLeave()
  const {
    error,
    loading,
    summaries,
    getSummaries,
    exportToCsv,
    errorPopupOpen,
    setErrorPopupOpen
  } = useSummaries()
  const { company } = useAuth()
  const [columns, setColumns] = useState([])
  const [locales, setLocales] = useState([])
  const [pageNumber, setPageNumber] = useState(DEFAULT_VALUES.PAGE_NUMBER)
  const [searchText, setSearchText] = useState(DEFAULT_VALUES.SEARCH_TEXT)
  const [sortOptions, setSortOptions] = useState(DEFAULT_VALUES.SORT)
  const [filters, setFilters] = useState(DEFAULT_VALUES.FILTERS)
  const [filterOptions, setFilterOptions] = useState(DEFAULT_VALUES.FILTER_OPTIONS)
  const pageCount = Math.ceil(summaries.total / DEFAULT_VALUES.ROWS_PER_PAGE) || 0

  const handleChangePage = (event) => {
    setPageNumber(event.selected + 1)
  }

  const handleSetSearchText = (value) => {
    setSearchText(value)
    setPageNumber(DEFAULT_VALUES.PAGE_NUMBER)
  }

  const handleSetSortOptions = (value) => {
    setSortOptions(value)
  }

  const handleSetFilters = (value) => {
    setFilters(value)
    setPageNumber(DEFAULT_VALUES.PAGE_NUMBER)
  }

  const handleSetFilterOptions = (value) => {
    setPageNumber(DEFAULT_VALUES.PAGE_NUMBER)
    setFilterOptions(value)
  }

  const handleSetPageNumber = (value) => {
    setPageNumber(value)
  }

  const exportCsv = () => {
    const companyId = company ? company._id : getCompanyId()

    exportToCsv({
      companyId,
      output: OUTPUT_TYPE.BY_JURISDICTION,
      product: filterOptions.paidLeaveType,
      stateCodeFilter: filters.state ? filters.state.locale.state : undefined,
      sortDirection: sortOptions.direction,
      sortField: SORT_OPTIONS[sortOptions.field],
      searchText
    })
  }

  useEffect(() => {
    if (isMounted) {
      if (paidLeaveData) {
        const dataFilteredByPaidLeaveType = paidLeaveData
          .filter((jurisdiction) => jurisdiction.type !== 'group'
            && jurisdiction.topics.find((topic) => topic.product === filterOptions.paidLeaveType))
        const states = [...new Set(dataFilteredByPaidLeaveType.map((location) => location.state))]
        const filteredLocales = filterLocales(LEGACY_PRODUCTS.MINWAGE)
          .filter((locale) => states.includes(locale.locale.state))
        setLocales(filteredLocales)
      }
    }
  }, [paidLeaveData])

  useEffect(() => {
    if (isMounted) {
      let paidLeaveType = filterOptions.paidLeaveType
      const queryParams = queryString.parse(props.location.search)
      if (queryParams && queryParams.type) {
        paidLeaveType = queryParams.type
        setFilterOptions({
          ...filterOptions,
          paidLeaveType: queryParams.type
        })
      }
      const product = getProductById(paidLeaveType)
      if (!product) throw new Error(`Paid Leave product ${paidLeaveType} not found.`)
      setSelectedProduct(product)
    }
  }, [])

  useEffect(() => {
    if (isMounted && company) {
      getSummaries({
        pageNumber,
        pageSize: DEFAULT_VALUES.ROWS_PER_PAGE,
        sort: SORT_OPTIONS[sortOptions.field],
        sortOrder: sortOptions.direction,
        searchText,
        stateCodeFilter: filters.state ? filters.state.locale.state : undefined,
        product: filterOptions.paidLeaveType,
        companyId: company._id
      })
    }
  }, [
    pageNumber,
    filters,
    filterOptions,
    sortOptions,
    searchText
  ])

  useEffect(() => {
    if (isMounted) {
      setColumns(
        createColumns({
          sortOptions,
          filterOptions,
          eventHandlers: {
            setSortOptions,
            setFilterOptions,
            setPageNumber
          }
        })
      )
    }
  }, [summaries])

  if (error) {
    return (
      <DefaultErrorMessage
        error={error}
        data-testid="error-container"
      />
    )
  }

  return (
    <Fragment>
      <DataTable
        {...{
          pageCount,
          isLoading: loading,
          recordType: '',
          showSearch: true,
          showExport: true,
          showLocaleFilter: true,
          showPaidLeaveFilter: true,
          isScrollable: true,
          isHeaderSticky: true,
          eventHandlers: {
            handleChangePage,
            setSearchText: handleSetSearchText,
            setSortOptions: handleSetSortOptions,
            setFilterOptions: handleSetFilterOptions,
            setFilters: handleSetFilters,
            setPageNumber: handleSetPageNumber,
            export: exportCsv
          },
          columns,
          filters,
          sortOptions,
          filterOptions,
          pageNumber,
          searchText,
          locales,
          total: summaries.total,
          data: summaries.summaries || [],
          rowsPerPage: DEFAULT_VALUES.ROWS_PER_PAGE
        }}
      />
      <CSVDownloadError open={errorPopupOpen} onClose={() => setErrorPopupOpen(false)} />
    </Fragment>
  )
}

export default PaidLeaveSummaryTable
